/**
 * Pure re-sequence of the grid list to a server-owned order (DV-2859 Caption
 * Sort: the photoset the POST returns carries the new `assetUUIDs`). Shared
 * by the `applyPhotosetOrder` reducer and the hook that calls it, the same
 * way `reorderByPhotoUUID` is shared by the drag path, so "did anything
 * move" is answered once, from the list the reducer actually applies.
 *
 * The server list is the FULL photoset order, including non-photo (Zillow
 * url) assets the grid never holds — those are simply skipped. Grid items the
 * server list does not name (a just-uploaded photo the load-time list
 * predates) keep their current relative order and follow the named ones,
 * mirroring the server's own "unlisted tails, in current order" rule.
 *
 * Returns the input array (same reference) when no position changes, so a
 * caller can distinguish "sorted" from "already in order" by identity.
 */
export function orderByAssetUUIDs<T extends { assetUUID: string }>(
  list: T[],
  assetUUIDs: readonly string[]
): T[] {
  const rank = new Map<string, number>();
  assetUUIDs.forEach((assetUUID, index) => {
    if (!rank.has(assetUUID)) rank.set(assetUUID, index);
  });
  const named = list.filter((item) => rank.has(item.assetUUID));
  const unlisted = list.filter((item) => !rank.has(item.assetUUID));
  // Array.prototype.sort is stable (ES2019), so ties cannot reorder — and
  // the ranks are unique by construction anyway.
  named.sort(
    (a, b) =>
      (rank.get(a.assetUUID) as number) - (rank.get(b.assetUUID) as number)
  );
  const next = [...named, ...unlisted];
  const unchanged = next.every((item, index) => item === list[index]);
  return unchanged ? list : next;
}
